import { Component, inject } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ColumnSelection,
  PrintColumnSelectionDialogData,
  PrintColumnSelectionMode,
  PrintColumnSelectionResult
} from '../../models/dialog.model';
import { getPrintColumnHeadings, getPrintColumnKey, getPrintColumnParts } from '../../utils/print-column.util';

@Component({
  selector: 'app-print-column-selection-dialog',
  templateUrl: './print-column-selection-dialog.component.html',
  styleUrl: './print-column-selection-dialog.component.scss'
})
export class PrintColumnSelectionDialogComponent {
  readonly dialogRef = inject(MatDialogRef<PrintColumnSelectionDialogComponent>);
  readonly data = inject<PrintColumnSelectionDialogData>(MAT_DIALOG_DATA);
  protected readonly mode: PrintColumnSelectionMode = this.data?.mode ?? 'kitLotInstrumentBead';

  protected readonly columns: ColumnSelection[] = (this.data?.columns ?? [])
    .map(column => ({ ...column }));
  protected readonly selectedKeys = new Set<string>(
    this.columns.filter(column => column.isSelected).map(column => getPrintColumnKey(column))
  );

  /** Headings and cells come from one place so the two never drift out of step. */
  protected readonly headings = getPrintColumnHeadings(this.mode);

  /** Only offered when the study has both halves; see PrintColumnSelectionDialogData. */
  protected readonly offerResultBasis = this.data?.offerResultBasis ?? false;
  /** Both checked, so the report is the one the server printed before this was offered. */
  protected includeUnadjusted = true;
  protected includeAdjusted = true;

  /** Whether to ask for the material and batch, and whether to insist on them. */
  protected readonly offerPaginationInfo = !!this.data?.paginationInfo;
  protected readonly paginationInfoRequired = this.data?.paginationInfo === 'required';
  /** Transient: the study does not record them, so each report is printed with what is typed here. */
  protected materialNumber = '';
  protected batchNumber = '';

  /** Only offered where it is offered at all; see PrintColumnSelectionDialogData. */
  protected readonly offerRunDetails = this.data?.runDetailsDefault !== undefined;
  protected includeRunDetails = this.data?.runDetailsDefault ?? false;

  /**
   * Each option group collapses to its summary line, so the dialog opens short enough to
   * fit a laptop screen. The line has to state the setting, not just name the section —
   * collapsed, it is the only thing saying what the report will be printed with.
   */
  protected paginationInfoSummary(): string {
    const entered = [this.materialNumber.trim(), this.batchNumber.trim()].filter(Boolean);
    if (!entered.length) {
      return this.paginationInfoRequired ? 'Required' : 'Not entered';
    }
    return entered.join(' / ');
  }

  protected runDetailsSummary(): string {
    return this.includeRunDetails ? 'Worklist and report items' : 'Results only';
  }

  protected resultBasisSummary(): string {
    if (this.includeUnadjusted && this.includeAdjusted) {
      return 'Unadjusted and adjusted';
    }
    if (this.includeUnadjusted) {
      return 'Unadjusted only';
    }
    return this.includeAdjusted ? 'Adjusted only' : 'None selected';
  }

  protected cells(column: ColumnSelection): (string | number)[] {
    return getPrintColumnParts(column, this.mode);
  }

  toggleColumn(column: ColumnSelection, event: MatCheckboxChange): void {
    const key = getPrintColumnKey(column);
    if (event.checked) {
      this.selectedKeys.add(key);
    } else {
      this.selectedKeys.delete(key);
    }
  }

  selectAll(): void {
    this.columns.forEach(column => this.selectedKeys.add(getPrintColumnKey(column)));
  }

  clearAll(): void {
    this.selectedKeys.clear();
  }

  isChecked(column: ColumnSelection): boolean {
    return this.selectedKeys.has(getPrintColumnKey(column));
  }

  isSelectionEmpty(): boolean {
    return this.selectedKeys.size === 0;
  }

  toggleUnadjusted(event: MatCheckboxChange): void {
    this.includeUnadjusted = event.checked;
  }

  toggleAdjusted(event: MatCheckboxChange): void {
    this.includeAdjusted = event.checked;
  }

  /**
   * A report of neither half is a blank document, so Apply is disabled rather than
   * explained afterwards — unlike an empty column selection, the invalid state is
   * visible in this dialog and can be corrected without reopening it.
   */
  isResultBasisEmpty(): boolean {
    return this.offerResultBasis && !this.includeUnadjusted && !this.includeAdjusted;
  }

  toggleRunDetails(event: MatCheckboxChange): void {
    this.includeRunDetails = event.checked;
  }

  /**
   * The server rejects a report that requires them without them, so Apply is disabled
   * rather than letting the request fail after the wait for a render. Where they are
   * optional there is nothing to block on — blank simply prints no material in the footer.
   */
  isPaginationInfoIncomplete(): boolean {
    return this.paginationInfoRequired && !(this.materialNumber.trim() && this.batchNumber.trim());
  }

  /** One predicate for the Apply button, so the template states no logic of its own. */
  isApplyDisabled(): boolean {
    return this.isResultBasisEmpty() || this.isPaginationInfoIncomplete();
  }

  applySelection(): void {
    const updatedColumns = this.columns.map(column => ({
      ...column,
      isSelected: this.selectedKeys.has(getPrintColumnKey(column))
    }));
    // Both halves when the choice was not offered, so the caller has one shape to read.
    const materialNumber = this.materialNumber.trim();
    const batchNumber = this.batchNumber.trim();
    const result: PrintColumnSelectionResult = {
      columns: updatedColumns,
      includeUnadjusted: this.offerResultBasis ? this.includeUnadjusted : true,
      includeAdjusted: this.offerResultBasis ? this.includeAdjusted : true,
      includeRunDetails: this.offerRunDetails && this.includeRunDetails,
      // Omitted when blank rather than sent empty: where they are optional, a blank is the
      // operator declining them, and the server prints no material line for an absent one.
      ...(this.offerPaginationInfo && materialNumber ? { materialNumber } : {}),
      ...(this.offerPaginationInfo && batchNumber ? { batchNumber } : {})
    };
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackByKey(index: number, column: ColumnSelection): string {
    return getPrintColumnKey(column);
  }
}
